<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { normalizeDate, stylizeDate } from './calendarUtils';
import CalendarMonth from './CalendarMonth.vue';

/**
 * DatePicker component displays from and until dates.
 * Set by the AppCalendar component.
 *
 * PROPS
 * @property {String} label -- Displays a title above the component.
 * @property {Date} predefinedStart -- Displays a preset date
 * @property {Date} predefinedEnd -- Displays a preset date
 * @property {Object} disabledDates -- Passed through to AppCalendar
 */

const emit = defineEmits<{
	(e: 'selectedPeriod', period: { from: string | null; to: string | null }): void;
	(e: 'selectedDate', date: Date | string): void;
	(e: 'clearDates'): void;
	(e: 'applySelection', period: { from: string | null; to: string | null }): void;
}>();

const props = defineProps({
	label: {
		type: String,
	},
	placeholder: {
		type: Object,
		required: true,
		validator(value) {
			if (typeof value !== 'object') return false;
			if (!value.fromLabel || typeof value.fromLabel !== 'string') return false;
			if (value.toLabel && typeof value.toLabel !== 'string') return false;

			return true;
		},
	},
	predefinedStart: [Date, String],
	predefinedEnd: [Date, String],
	disabledDates: Object,
	hasError: {
		type: Boolean,
		default: false,
	},
	errorLabel: String,
	disabled: {
		type: Boolean,
		default: false,
	},
	isShowingControls: {
		type: Boolean,
		default: true,
	},
	isShowingRangeToggle: {
		type: Boolean,
		default: true,
	},
	hasRange: {
		type: Boolean,
		default: true,
	},
});

const showCalendar = ref(false);
const datesSelected = ref(false);
const startDay = ref();
const endDay = ref();
const hasRange = ref(props.hasRange);

const clearSelection = () => {
	startDay.value = null;
	endDay.value = null;
};

const hideCalendar = () => {
	showCalendar.value = false;
	if (!datesSelected.value && !props.predefinedStart) {
		clearSelection();
	}
};

const resetSelectedDates = () => {
	datesSelected.value = false;
	clearSelection();
	emit('clearDates');
};

const handleStartDaySelected = (date: Date) => {
	if (!hasRange.value) {
		emit('selectedDate', date);
	}
	startDay.value = stylizeDate(date);
};

const handleEndDaySelected = (date: Date) => {
	if (!hasRange.value) {
		clearSelection();
		// If singleDate is true, treat endDay selection as startDay selection
		handleStartDaySelected(date);
		return;
	}

	endDay.value = stylizeDate(date);

	emit('selectedPeriod', {
		from: normalizeDate(startDay.value),
		to: normalizeDate(endDay.value),
	});
};

const handleToggleHasRange = (hasRangeValue: boolean) => {
	hasRange.value = !hasRangeValue;
	if (!datesSelected.value) {
		clearSelection();
	} else {
		resetSelectedDates();
	}
};

const handleSelectedDates = () => {
	emit('applySelection', {
		from: normalizeDate(startDay.value),
		to: endDay.value ? normalizeDate(endDay.value) : null,
	});
	showCalendar.value = false;
	datesSelected.value = true;
};

onMounted(() => {
	if (props.predefinedStart) startDay.value = stylizeDate(props.predefinedStart);
	if (props.predefinedEnd) endDay.value = stylizeDate(props.predefinedEnd);
});

defineExpose({
	resetSelectedDates,
});
</script>

<template>
	<div class="datepicker" data-ts="datepicker" v-click-outside="hideCalendar">
		<!-- Title of component, displayed above the date display -->
		<div v-if="label" class="datepicker__label">{{ label }}</div>
		<!-- Date display "startDate" - "endDate" -->
		<div
			aria-label="open calendar"
			title="Show/Hide calendar"
			:class="[
				'datepicker__display',
				{ 'datepicker__display--error': hasError },
				{ 'datepicker__display--disabled': disabled },
			]"
			role="button"
			tabindex="0"
			aria-haspopup="true"
			@click="showCalendar = !showCalendar"
			@keydown.enter.prevent="!disabled && (showCalendar = !showCalendar)"
		>
			<span class="bx bx-calendar" />
			<div class="datepicker__display__dates">
				<p class="datepicker__display__dates__date">
					{{ startDay || placeholder.fromLabel }}
				</p>
				<template v-if="hasRange">
					<span class="bx bx-minus text-neutral-400 text-weight-medium" />
					<p class="datepicker__display__dates__date">
						{{ endDay || placeholder.toLabel }}
					</p>
				</template>
			</div>
		</div>
		<button
			v-show="datesSelected || predefinedStart"
			:disabled="disabled"
			aria-label="Clear dates"
			name="clearDatesBtn"
			class="clear-button"
			data-test="clearDatesButton"
			type="button"
			@click="resetSelectedDates"
		>
			<span class="bx bx-x text-md" />
		</button>
		<transition name="fade">
			<calendar-month
				v-if="showCalendar"
				id="calendarDropdown"
				:disabled-dates="disabledDates"
				:predefined-start="startDay"
				:predefined-end="endDay"
				:has-range="hasRange"
				:is-showing-controls="isShowingControls"
				:is-showing-range-toggle="isShowingRangeToggle"
				class="datepicker__calendar"
				@startDaySelected="handleStartDaySelected"
				@endDaySelected="handleEndDaySelected"
				@clearDates="clearSelection"
				@toggleHasRange="handleToggleHasRange"
				@applyUserSelection="handleSelectedDates"
			/>
		</transition>
		<p v-if="hasError" class="absolute text-error-700 text-sm">
			{{ errorLabel }}
		</p>
	</div>
</template>
<style lang="scss" scoped>
$date-picker-width: 260px;

.datepicker {
	$this: &;
	position: relative;
	width: $date-picker-width;

	&__label {
		font-size: $font-size-sm;
		font-weight: $font-weight-medium;
		margin-bottom: $spacing-1; // Margin placed here so it can be removed with the element.
	}

	&__display {
		width: $date-picker-width;
		height: $height-form-element;
		border: $border-width-1 $border-style $color-gray-200;
		border-radius: $border-radius-2;
		display: flex;
		align-items: center;
		padding: 0 $spacing-2;
		cursor: pointer;
		background-color: $color-white;

		@media (hover: hover) {
			&:hover:not([disabled]) {
				border-color: $color-input-hover;
			}
		}

		&:focus-visible:not([disabled]) {
			outline: $border-width-2 $border-style $color-focus;
		}

		&--error {
			border: $border-width-1 $border-style $color-error-700;
		}

		&--disabled {
			pointer-events: none;
			color: $color-neutral-400;
			background-color: $color-neutral-50;
		}

		&__dates {
			width: 100%;
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding-right: $spacing-4;
			font-size: $font-size;
			font-weight: $font-weight;

			&__date {
				width: 100%;
				max-height: $height-form-element;
				text-align: center;
				overflow: hidden;
				line-height: 1.2;
				font-size: $font-size;
			}
		}
	}

	&__calendar {
		position: absolute;
		z-index: $z-index-2;
		margin-top: $spacing-1;
	}
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 400ms;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
	opacity: 0;
}

.clear-button {
	position: absolute;
	height: 40px;
	width: 40px;
	font-size: $font-size-md;
	right: 0;
	bottom: -4px;
	margin-left: $spacing-1;
	border-radius: $border-radius-1;
	display: flex;
	align-items: center;
	justify-content: center;

	@media (hover: hover) {
		&:hover:not([disabled]) {
			color: $color-error-600;
		}
	}
}
</style>
